import { CalendarAdd01Icon, CalendarMinus01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Notification01Icon } from '@hugeicons/core-free-icons';
import { enhancedNotify } from '@pique/core/components/shared/notification-utils';
import {
  ActionItemsService,
  DEFAULT_PLANNED_FOCUS_MINUTES,
} from '@pique/core/modules/properties/services/action-items-service';
import type { SerializedTodoItem } from '@pique/core/modules/properties/services/schema';
import { posthog } from '@pique/core/posthog';
import { useService, WorkspaceService } from '@toeverything/infra';
import dayjs from 'dayjs';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  ActionItemDatePicker,
  type QuickDateOption,
} from './ActionItemDatePicker';
import { notifyFollowupReminderSet } from './followup-reminder';
import { REMINDER_MESSAGES, type ReminderState } from './reminder-constants';
import * as styles from './ScheduleMePopup.css';

const SCHEDULE_HOUR = 9;

interface ScheduleMePopupProps {
  item: SerializedTodoItem;
  isInFocusZone: boolean;
  calendarEvent: boolean;
  isScheduleOverdue?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  onOpenCalendarView?: (targetDate?: Date, itemId?: string) => void;
}

export const ScheduleMePopup: React.FC<ScheduleMePopupProps> = ({
  item,
  isInFocusZone,
  calendarEvent,
  isScheduleOverdue,
  onMenuOpenChange,
  onOpenCalendarView,
}) => {
  const actionItemsService = useService(ActionItemsService);
  const workspaceService = useService(WorkspaceService);

  const [isOpen, setIsOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<string | null>(null);
  const [scheduleTime, setScheduleTime] = useState<string>('09:00');
  const [notifyEnabled, setNotifyEnabled] = useState(false);

  const docCollection = workspaceService.workspace?.docCollection;
  const pageMeta = item.pageId
    ? docCollection?.meta.getDocMeta(item.pageId)
    : undefined;

  const notifTitle = useMemo(() => {
    const docTitle = (pageMeta?.title || '').toString();
    const itemTitle =
      typeof item.title === 'string' ? item.title : String(item.title || '');
    return itemTitle && itemTitle.trim().length
      ? itemTitle
      : docTitle || 'Reminder';
  }, [item.title, pageMeta?.title]);

  const defaultReminderMessage = REMINDER_MESSAGES.DEFAULT;

  const hasSchedule = Boolean(item.scheduledAt);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onMenuOpenChange?.(open);
      if (!open) {
        setScheduleDate(null);
        setScheduleTime('09:00');
        setNotifyEnabled(false);
      }
    },
    [onMenuOpenChange]
  );

  const handleDateChange = useCallback((date: string) => {
    setScheduleDate(date);
  }, []);

  const nextWeekDate = useMemo(() => {
    return dayjs().add(1, 'week').startOf('week').add(1, 'day');
  }, []);

  const nextMonthDate = useMemo(() => {
    return dayjs().add(1, 'month').startOf('month');
  }, []);

  const handleQuickSchedule = useCallback(
    (targetDate: dayjs.Dayjs) => {
      const scheduledDateObj = targetDate.hour(SCHEDULE_HOUR).minute(0).second(0).toDate();
      const scheduledAt = scheduledDateObj.toISOString();

      actionItemsService.updateById(item.id, {
        isFocused: true,
        scheduledAt,
        plannedDuration: DEFAULT_PLANNED_FOCUS_MINUTES,
      });

      posthog.capture('action_item_scheduled', {
        item_id: item.id,
        scheduled_at: scheduledAt,
      });

      handleOpenChange(false);
    },
    [actionItemsService, handleOpenChange, item.id]
  );

  const quickOptions = useMemo(
    (): QuickDateOption[] => [
      {
        label: 'Next week',
        onClick: () => handleQuickSchedule(nextWeekDate),
      },
      {
        label: 'Next month',
        onClick: () => handleQuickSchedule(nextMonthDate),
      },
    ],
    [handleQuickSchedule, nextWeekDate, nextMonthDate]
  );

  const handleSchedule = useCallback(async () => {
    if (!scheduleDate) return;

    const dateParts = scheduleDate.split('-').map(Number);
    const timeParts = scheduleTime.split(':').map(Number);
    const year = dateParts[0] ?? 0;
    const month = dateParts[1] ?? 1;
    const day = dateParts[2] ?? 1;
    const hours = timeParts[0] ?? 0;
    const minutes = timeParts[1] ?? 0;
    const scheduledDateObj = new Date(year, month - 1, day, hours, minutes);

    if (scheduledDateObj.getTime() <= Date.now()) {
      void enhancedNotify
        .error(
          {
            title: 'Invalid time',
            message: 'Please select a future date and time',
          },
          { duration: 3000, position: 'top-center' }
        )
        .catch(() => {});
      return;
    }

    const scheduledAt = scheduledDateObj.toISOString();

    actionItemsService.updateById(item.id, {
      isFocused: true,
      scheduledAt,
      plannedDuration: DEFAULT_PLANNED_FOCUS_MINUTES,
    });

    posthog.capture('action_item_scheduled', {
      item_id: item.id,
      scheduled_at: scheduledAt,
    });

    if (notifyEnabled) {
      const delayMinutes = Math.ceil((scheduledDateObj.getTime() - Date.now()) / 60_000);

      const newReminderState: ReminderState = {
        isSet: true,
        scheduledAt,
        minutes: delayMinutes,
      };

      actionItemsService.updateById(item.id, {
        followupReminder: {
          enabled: true,
          minutes: delayMinutes,
          scheduleAt: scheduledAt,
          message: defaultReminderMessage,
          channels: {
            in_app: true,
            push: true,
            email: false,
            chat: false,
            sms: false,
          },
        },
        reminderState: newReminderState,
      } as Partial<SerializedTodoItem>);

      await notifyFollowupReminderSet({ scheduledAt });

      posthog.capture('action_item_scheduled', {
        itemId: item.id,
        itemTitle: notifTitle,
        pageId: item.pageId,
        scheduledAt,
        delayMinutes,
        source: 'schedule_me_popup',
      });
    }

    handleOpenChange(false);
  }, [
    actionItemsService,
    defaultReminderMessage,
    handleOpenChange,
    item.id,
    item.pageId,
    notifyEnabled,
    notifTitle,
    scheduleDate,
    scheduleTime,
  ]);

  if (calendarEvent) {
    return null;
  }

  const footerContent = (
    <>
      <div className={styles.footerRow}>
        <button
          type="button"
          className={styles.notifyToggle}
          data-active={notifyEnabled || undefined}
          onClick={e => {
            e.stopPropagation();
            setNotifyEnabled(v => !v);
          }}
          title={notifyEnabled ? 'Notification enabled' : 'Add notification'}
        >
          <HugeiconsIcon icon={Notification01Icon} size={12} />
        </button>
        <span className={styles.notifyLabel}>
          {notifyEnabled ? 'Notify me' : 'Add reminder'}
        </span>
      </div>
      <button
        type="button"
        className={styles.scheduleButton}
        onClick={e => {
          e.stopPropagation();
          handleSchedule().catch(() => {});
        }}
        disabled={!scheduleDate}
      >
        {hasSchedule ? 'Reschedule' : 'Schedule'}
      </button>
    </>
  );

  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isInFocusZone && !isScheduleOverdue && onOpenCalendarView) {
        const targetDate = item.scheduledAt
          ? new Date(item.scheduledAt)
          : undefined;
        onOpenCalendarView(targetDate, item.id);
      } else {
        setIsOpen(v => !v);
      }
    },
    [isInFocusZone, isScheduleOverdue, item.id, item.scheduledAt, onOpenCalendarView]
  );

  const triggerTitle = useMemo(() => {
    if (isScheduleOverdue) return 'Reschedule';
    if (hasSchedule) return 'View in calendar';
    return 'Schedule';
  }, [hasSchedule, isScheduleOverdue]);

  return (
    <ActionItemDatePicker
      open={isOpen}
      onOpenChange={handleOpenChange}
      value={scheduleDate || undefined}
      onChange={handleDateChange}
      quickOptions={quickOptions}
      sectionLabel="Pick a day"
      showTimePicker={true}
      time={scheduleTime}
      onTimeChange={setScheduleTime}
      footerContent={footerContent}
      calendarMode="week"
      trigger={
        <button
          type="button"
          className={styles.trigger}
          data-open={isOpen || undefined}
          data-in-focus={isInFocusZone || undefined}
          data-overdue={isScheduleOverdue || undefined}
          onClick={handleTriggerClick}
          title={triggerTitle}
        >
          <HugeiconsIcon
            icon={hasSchedule ? CalendarMinus01Icon : CalendarAdd01Icon}
            size={12}
          />
        </button>
      }
    />
  );
};
